/**
 * PlatformFichaDispositivos — ficha de detalle de un dispositivo (caja/screen/vlt)
 * en un panel inspector lateral del tab Dispositivos (centro de operaciones, 2026-06).
 *
 * EL OLVIDO: el tab Dispositivos solo tenía el modal gigante "Editar Dispositivo".
 * Faltaba poder VER el dispositivo (estado, nodo, sorteos, fleet) sin entrar a editar.
 * Esta ficha es el inspector de solo-vista + atajos a las acciones que ya existen.
 *
 * Enfoque ADITIVO y reversible: en vez de reestructurar el layout maduro del tab
 * (filtros + pendientes + tabla + paginación), se muestra en un panel ASIDE flotante
 * a la derecha que se abre/cierra. Si fallara, es un panel que no aparece — no rompe
 * la tabla existente.
 *
 * AGRUPACIÓN data-driven (mismo criterio que la ficha de entidades): si el
 * dispositivo no tiene un dato, ni se muestra el grupo.
 *   - IDENTIDAD (siempre): nombre, serial, tipo + subtipo, nodo/PV.
 *   - ESTADO (siempre): online/offline, activo/pausado.
 *   - SORTEOS (solo si sorteoConfigs): juegos asignados.
 *   - ACCIONES: Editar (primaria) + Avanzado (pausar/reactivar, desvincular, borrar,
 *     comandar TV si screen, fleet si android). Reusa los data-action dispositivos:*.
 *
 * Campos reales por dispositivo (de la fila del listado):
 *   id, nombre, serial, tipo (caja/screen/vlt), caja_subtipo, config.tipoConexion,
 *   nodo_nombre, parent_nombre, online, activo, sorteoConfigs[].
 */

import { icon } from '../utils/icon.js';
import { escapeHtmlParaInyeccionSegura } from '../utils/escape-html-para-inyeccion-segura.js';
import { t } from '../services/i18n-admin.js';
import { App, Api } from '../admin.js';

const ESC = escapeHtmlParaInyeccionSegura;
const PANEL_ID = 'dispositivoFichaPanel';

export const PlatformFichaDispositivos = {
    /**
     * Muestra la ficha del dispositivo.
     * @param {Object} d - el dispositivo (fila ya cargada por el listado).
     * @param {string} [panelId] - si se pasa, renderiza DENTRO de ese panel
     *   (ej. 'nodoDetailPanel': el inspector derecho del árbol de nodos, igual
     *   que la ficha de entidades al clickear un cajero). Sin él, usa el panel
     *   aside flotante propio del tab Dispositivos.
     */
    mostrar(d, panelId = null) {
        if (!d) return;
        // Modo embebido: pintar en el inspector que nos pasen (el árbol reusa
        // su nodoDetailPanel). Sin botón de cerrar — el panel es persistente.
        if (panelId) {
            const destino = document.getElementById(panelId);
            if (!destino) return;
            destino.innerHTML = `<div class="dispositivo-ficha-body">${this._renderBody(d)}</div>`;
            return;
        }
        // Modo aside flotante (tab Dispositivos).
        let panel = document.getElementById(PANEL_ID);
        if (!panel) {
            panel = document.createElement('aside');
            panel.id = PANEL_ID;
            panel.className = 'dispositivo-ficha-panel';
            document.body.appendChild(panel);
        }
        panel.innerHTML = this._render(d);
        panel.classList.add('abierto');
    },

    cerrar() {
        const panel = document.getElementById(PANEL_ID);
        if (panel) panel.classList.remove('abierto');
    },

    // ── Render ────────────────────────────────────────────────────────────
    // Aside flotante (tab Dispositivos): cabecera con cerrar + cuerpo.
    _render(d) {
        return `
            <div class="dispositivo-ficha-cabecera">
                <h3>${ESC(d.nombre || t('Dispositivo ') + d.id)}</h3>
                <button class="btn-icon" data-action="dispositivos:cerrarFicha" title="${t('Cerrar')}">${icon('x')}</button>
            </div>
            <div class="dispositivo-ficha-body">${this._renderBody(d)}</div>
        `;
    },

    // Cuerpo de la ficha (SSOT, reusado por el aside y el modo embebido en árbol).
    _renderBody(d) {
        const activo = d.activo !== 0;
        const esWeb = this._esWeb(d);
        const esAndroid = !esWeb;
        const esScreen = d.tipo === 'screen';
        return `
            ${this._grupoIdentidad(d)}
            ${this._grupoEstado(d, activo)}
            ${this._grupoCajeros(d)}
            ${this._grupoSorteos(d)}
            ${this._grupoAcceso(d, { esAndroid, esWeb, esScreen })}
            ${this._grupoAcciones(d, { activo, esScreen, esAndroid })}
        `;
    },

    /**
     * Clasificación web vs android — criterio SSOT sellado (mismo que el árbol):
     * una caja/screen es WEB salvo que su subtipo sea explícitamente 'android'.
     * Así las legacy con subtipo NULL caen en web (no en android), evitando el
     * bug de clasificación que tenía la versión anterior (caja_subtipo === 'web').
     */
    _esWeb(d) {
        return (
            (d.tipo === 'caja' && d.caja_subtipo !== 'android') ||
            (d.tipo === 'screen' && (d.tv_subtipo ?? this._tipoConexion(d)) !== 'android')
        );
    },

    _tipoConexion(d) {
        if (d.tipo !== 'screen') return null;
        try {
            const cfg = typeof d.config === 'string' ? JSON.parse(d.config) : d.config || null;
            return cfg?.tipoConexion || null;
        } catch (_e) {
            return null;
        }
    },

    _grupoIdentidad(d) {
        const subtipo = d.tipo === 'caja' ? d.caja_subtipo : this._tipoConexion(d);
        const breadcrumb = [d.parent_nombre, d.nodo_nombre].filter(Boolean).map(ESC).join(' › ');
        // Ubicación: solo cuando hay breadcrumb (lo trae el listado enriquecido
        // del tab). Desde el árbol el objeto no lo trae — y ahí el nodo padre ya
        // se ve en el propio árbol, así que omitir la fila es honesto (data-driven),
        // mejor que mostrar "Sin nodo" engañoso.
        const filaUbicacion = breadcrumb
            ? `<dt>${t('Ubicación')}</dt><dd><span class="dispositivo-ficha-nodo-link" data-action="dispositivos:irAlNodo" data-id="${d.nodo_id}">${breadcrumb}</span></dd>`
            : '';
        // Dirección física: viene del PV (config del nodo), de solo lectura acá — se
        // edita desde el modal de nodo. Solo aparece si alguien ya la cargó.
        const filaDireccion = d.direccion_fisica
            ? `<dt>${t('Dirección')}</dt><dd>${ESC(d.direccion_fisica)}</dd>`
            : '';
        // Nota de ubicación: campo LIBRE propio del dispositivo, editable inline acá mismo —
        // para el caso frecuente de TV sin dirección de PV clara ("la tele arriba del
        // mostrador, al lado del freezer"). Sin esto, soporte no tiene ningún dato de texto
        // libre para anotar "cuál físicamente es cuál" cuando llaman confundidos.
        return `
            <dl class="dispositivo-ficha-dl">
                <dt>${t('Tipo')}</dt><dd>${ESC(d.tipo)}${subtipo ? ' · ' + ESC(subtipo) : ''}</dd>
                <dt>${t('Serial')}</dt><dd><code>${ESC(d.serial || '-')}</code></dd>
                ${filaUbicacion}
                ${filaDireccion}
                <dt>${t('Nota de ubicación')}</dt>
                <dd>
                    <div style="display:flex; gap:6px; align-items:center;">
                        <input type="text" class="form-input" id="dispositivoNotaUbicacionInput"
                               data-id="${d.id}" value="${ESC(d.nota_ubicacion || '')}"
                               placeholder="${t('Ej: al lado del freezer, frente a la caja')}"
                               style="font-size:12px; padding:4px 8px;">
                        <button class="btn btn-secondary btn-icon" data-action="dispositivos:guardarNotaUbicacion" data-id="${d.id}" title="${t('Guardar')}">
                            ${icon('check')}
                        </button>
                    </div>
                </dd>
            </dl>
        `;
    },

    _grupoEstado(d, activo) {
        // Estado de instalación (vale): lo deriva el backend del árbol.
        //   operativo    = instalado y con token (opera money-path).
        //   sin_instalar = vale generado, falta canjear/instalar el APK.
        //   legacy       = dispositivo viejo sin vale (pre-aprovisionamiento).
        const INSTALACION = {
            operativo: { txt: t('Operativo'), cls: 'success' },
            sin_instalar: { txt: t('Sin instalar'), cls: 'warning' },
            legacy: { txt: t('Legacy (sin vale)'), cls: 'muted' },
        };
        const inst = d.estadoVale ? INSTALACION[d.estadoVale] : null;
        // Las acciones de acceso (QR/enlace/bajar/regenerar) viven en _grupoAcceso.
        const filaInstalacion = inst
            ? `<dt>${t('Instalación')}</dt><dd><span class="status-badge ${inst.cls}">${inst.txt}</span></dd>`
            : '';
        // Última conexión: si está offline pero tenemos last_seen, decir hace cuánto
        // (más útil que "offline" a secas para un dispositivo caído).
        const rel = !d.online && d.last_seen ? this._tiempoRelativo(d.last_seen) : null;
        const filaUltimaConexion = rel ? `<dt>${t('Última conexión')}</dt><dd>${ESC(rel)}</dd>` : '';
        // Login de cajero (solo tipo 'caja'): distinto del heartbeat en vivo — sobrevive
        // offline. Si nunca logueó, se dice explícito (antes no había NINGÚN dato para
        // ese caso: "caja autorizada, pantalla en blanco, ¿alguna vez se usó?").
        const filaLoginCajero =
            d.tipo === 'caja'
                ? d.ultimo_login_cajero_at
                    ? `<dt>${t('Último login de cajero')}</dt><dd>${ESC(this._tiempoRelativo(d.ultimo_login_cajero_at))}</dd>`
                    : `<dt>${t('Login de cajero')}</dt><dd><span class="status-badge warning">${t('Nunca logueó un cajero acá')}</span></dd>`
                : '';
        return `
            <div class="dispositivo-ficha-grupo">
                <h4>${icon('activity')} ${t('Estado')}</h4>
                <dl class="dispositivo-ficha-dl">
                    <dt>${t('Conexión')}</dt><dd><span class="status-badge ${d.online ? 'success' : 'error'}">${d.online ? t('Online') : t('Offline')}</span></dd>
                    ${filaUltimaConexion}
                    <dt>${t('Operación')}</dt><dd><span class="status-badge ${activo ? 'success' : 'error'}">${activo ? t('Activo') : t('Pausado')}</span></dd>
                    ${filaInstalacion}
                    ${filaLoginCajero}
                </dl>
            </div>
        `;
    },

    /**
     * Grupo ACCESO — QR / enlace / bajar / regenerar, INLINE (sin modal).
     * REGLA (decisión del dueño): los botones de VER el artefacto (Ver QR, Copiar
     * enlace, Bajar APK) se muestran SOLO si hay un artefacto vigente que ver —
     * sino dan 409/error feo. "Regenerar" SIEMPRE está (es el que CREA el
     * artefacto): vuelve a poner el dispositivo en estado instalable.
     *   - Android `sin_instalar` (vale por canjear) → hay APK que instalar: Ver QR · Copiar · Bajar · Regenerar.
     *   - Android operativo/legacy (ya instalado, sin vale vigente) → mensaje + solo Regenerar.
     *   - Web con token pendiente (link sin canjear) → Ver QR · Copiar · Regenerar.
     *   - Web sin token → solo Regenerar (crea el primer link).
     * El resultado se pinta en `#acceso-artefacto-<id>`.
     */
    _grupoAcceso(d, { esAndroid }) {
        // VLT u otros tipos sin artefacto de acceso: no mostrar el grupo.
        if (d.tipo !== 'caja' && d.tipo !== 'screen') return '';
        const webTieneToken = d.status === 'authorized' || d.tieneToken === 1;
        // ¿Hay un artefacto vigente que VER? Android: solo si el vale está por
        // canjear (sin_instalar). Web: solo si hay token pendiente sin usar.
        const hayArtefacto = esAndroid ? d.estadoVale === 'sin_instalar' : webTieneToken;
        const dataAttrs =
            `data-id="${d.id}" data-android="${esAndroid ? '1' : '0'}" ` +
            `data-estado-vale="${ESC(d.estadoVale || '')}" data-tiene-token="${webTieneToken ? '1' : '0'}" ` +
            `data-nombre="${ESC(d.nombre || d.serial || '')}" data-pv="${ESC(d.nodo_nombre || '')}"`;

        // Botones de VER artefacto: solo si existe uno vigente.
        let botonesVer = '';
        if (hayArtefacto) {
            botonesVer =
                `<button class="btn btn-sm btn-primary" data-action="dispositivos:verQrAcceso" data-id="${d.id}">${icon('qr-code')} ${t('Ver QR')}</button>` +
                `<button class="btn btn-sm btn-secondary" data-action="dispositivos:copiarEnlaceAcceso" data-id="${d.id}">${icon('clipboard-list')} ${t('Copiar enlace')}</button>` +
                (esAndroid
                    ? `<button class="btn btn-sm btn-secondary" data-action="dispositivos:bajarApk" data-id="${d.id}">${icon('download')} ${t('Bajar APK')}</button>`
                    : '');
        }

        // Regenerar: SIEMPRE. Reusa el flujo correcto (android operativo→reasignar,
        // android sin_instalar→re-hornear el mismo vale, web→regenerar link).
        // Web: si NO hay link vigente → "Generar link" (obtenerAcceso, crea el
        // primero, no destructivo). Si YA hay → "Regenerar" (forzar=true: crea uno
        // nuevo e invalida el viejo, con confirmación). Ver QR/Copiar NO regeneran:
        // muestran el vigente (endpoint idempotente).
        const regenAction = esAndroid
            ? d.estadoVale === 'sin_instalar'
                ? 'dispositivos:hornearApk'
                : 'dispositivos:reasignarAparato'
            : hayArtefacto
              ? 'dispositivos:regenerarLinkWeb'
              : 'dispositivos:obtenerAcceso';
        const etiquetaRegen = hayArtefacto ? t('Regenerar') : esAndroid ? t('Generar APK (QR)') : t('Generar link');
        // Con artefacto: 'Regenerar' es destructivo (invalida el anterior) → estilo
        // discreto/peligro, separado de Ver/Copiar. Sin artefacto: es el CTA para
        // crear el primero → primary.
        const claseRegen = hayArtefacto ? 'btn-regen-peligro' : 'btn-primary';
        const btnRegen = `<button class="btn btn-sm ${claseRegen}" data-action="${regenAction}" data-id="${d.id}" data-nombre="${ESC(d.nombre || d.serial || '')}" data-pv="${ESC(d.nodo_nombre || '')}" data-tiene-token="${webTieneToken ? '1' : '0'}">${icon('refresh-cw')} ${etiquetaRegen}</button>`;

        // Sin artefacto vigente: aclarar por qué (honesto, no botones que fallan).
        const aviso = hayArtefacto
            ? ''
            : `<div class="dispositivo-acceso-instrucciones">${
                  esAndroid
                      ? t('Este aparato ya está instalado. Generá un APK nuevo (identidad nueva) si necesitás reinstalarlo o moverlo a otro equipo.')
                      : t('Generá el link de acceso para entregárselo al operador del dispositivo.')
              }</div>`;

        // Layout: acciones de VER (QR/Copiar/Bajar) juntas en una fila; Regenerar
        // en su PROPIA fila debajo, separado y discreto (acción destructiva: crea
        // un link nuevo e invalida el anterior). Si no hay artefacto, no hay
        // botonesVer → solo el de generar (que actúa como CTA principal).
        const filaVer = botonesVer ? `<div class="dispositivo-ficha-acciones">${botonesVer}</div>` : '';
        const filaRegen = `<div class="dispositivo-acceso-regen-fila">${btnRegen}</div>`;
        return `
            <div class="dispositivo-ficha-grupo dispositivo-ficha-acceso" ${dataAttrs}>
                <h4>${icon(esAndroid ? 'qr-code' : 'globe')} ${t('Acceso del dispositivo')}</h4>
                <div id="acceso-artefacto-${d.id}" class="dispositivo-acceso-artefacto"></div>
                ${aviso}
                ${filaVer}
                ${filaRegen}
            </div>
        `;
    },

    // Cajeros que operan esta caja (los adjunta el árbol en d.cajerosVinculados).
    // Solo cajas los tienen; data-driven: sin cajeros, no se muestra el grupo.
    // Cada uno enlaza a su propia ficha de usuario en el inspector.
    _grupoCajeros(d) {
        const cajeros = d.cajerosVinculados;
        if (!cajeros || cajeros.length === 0) return '';
        const items = cajeros
            .map(
                u => `<li class="dispositivo-ficha-cajero" data-action="nodos:verFichaUsuario"
                        data-id="${u.id}" data-usuario="${ESC(JSON.stringify(u))}">
                        ${icon('user')} <span>${ESC(u.nombre || u.email || 'Cajero ' + u.id)}</span>
                      </li>`
            )
            .join('');
        return `
            <div class="dispositivo-ficha-grupo">
                <h4>${icon('users')} ${t('Cajeros')} (${cajeros.length})</h4>
                <ul class="dispositivo-ficha-cajeros">${items}</ul>
            </div>
        `;
    },

    // Tiempo relativo compacto en español, sin dependencias (para last_seen).
    _tiempoRelativo(iso) {
        try {
            const t = new Date(iso).getTime();
            if (!t) return null;
            const segundos = Math.floor((Date.now() - t) / 1000);
            if (segundos < 60) return t('hace instantes');
            const min = Math.floor(segundos / 60);
            if (min < 60) return t('hace') + ` ${min} ` + t('min');
            const horas = Math.floor(min / 60);
            if (horas < 24) return t('hace') + ` ${horas} h`;
            const dias = Math.floor(horas / 24);
            return t('hace') + ` ${dias} ` + (dias > 1 ? t('días') : t('día'));
        } catch (_e) {
            return null;
        }
    },

    _grupoSorteos(d) {
        if (!d.sorteoConfigs || d.sorteoConfigs.length === 0) return '';
        const lista = d.sorteoConfigs.map((c) => ESC(c.nombre)).join(', ');
        return `
            <div class="dispositivo-ficha-grupo">
                <h4>${icon('gamepad-2')} ${t('Sorteos asignados')}</h4>
                <p class="dispositivo-ficha-sorteos">${lista}</p>
            </div>
        `;
    },

    // Acciones reales que el sistema YA permite sobre un dispositivo autorizado,
    // mostradas según tipo (data-driven, no inventadas):
    //   - Editar: siempre (modal de config completo).
    //   - Comandar: solo screen (TV) — reload/mensaje/reiniciar; +OSD/unlock/fleet
    //     update/limpiar webview si es TV-Android (el modal los filtra).
    //   - Fleet: solo dispositivos con bridge nativo (POS Android, TV Android) —
    //     heartbeat, updates, crashes, GPS, sesiones.
    //   - Avanzado (sensible): pausar/reactivar, desvincular, borrar.
    // CSS: btn-secondary para no-primarias (sin variante = botón blanco sin
    // estilo, el bug reportado); btn-danger para borrar.
    _grupoAcciones(d, { activo, esScreen, esAndroid }) {
        const toggleIcon = activo ? 'pause' : 'play';
        const toggleTxt = activo ? t('Pausar') : t('Reactivar');
        const fleet = esAndroid
            ? `<button class="btn btn-sm btn-secondary" data-action="dispositivos:openFleetInfo" data-id="${d.id}" data-tipo="${ESC(d.tipo)}">${icon('activity')} ${t('Fleet')}</button>`
            : '';
        const comandar = esScreen
            ? `<button class="btn btn-sm btn-secondary" data-action="dispositivos:tvComando" data-id="${d.id}">${icon('monitor')} ${t('Comandar TV')}</button>`
            : '';
        return `
            <div class="dispositivo-ficha-acciones">
                <button class="btn btn-sm btn-primary" data-action="dispositivos:openModal" data-id="${d.id}">${icon('edit')} ${t('Editar')}</button>
                ${comandar}
                ${fleet}
            </div>
            <details class="dispositivo-ficha-avanzado">
                <summary>${t('Avanzado')}</summary>
                <div class="dispositivo-ficha-avanzado-body">
                    <button class="btn btn-sm btn-secondary" data-action="dispositivos:toggleActivo" data-id="${d.id}" data-activo="${activo ? '1' : '0'}">${icon(toggleIcon)} ${toggleTxt}</button>
                    <button class="btn btn-sm btn-secondary" data-action="dispositivos:reset" data-id="${d.id}">${icon('unlink')} ${t('Desvincular')}</button>
                    <button class="btn btn-sm btn-danger" data-action="dispositivos:delete" data-id="${d.id}">${icon('trash-2')} ${t('Borrar')}</button>
                </div>
            </details>
        `;
    },

    /**
     * Guarda la nota de ubicación libre (config.notaUbicacion) de un dispositivo —
     * campo de texto para identificar físicamente cuál es cuál cuando soporte/campo
     * llama confundido. PATCH mínimo (solo esa clave de config, deepMerge en backend).
     */
    async guardarNotaUbicacion(id) {
        const input = document.getElementById('dispositivoNotaUbicacionInput');
        if (!input) return;
        const nota = input.value.trim();
        try {
            await Api.updateDispositivo(id, { config: { notaUbicacion: nota } });
            App.toast(t('Nota guardada'), 'success');
        } catch (err) {
            App.toast(err.message || t('No se pudo guardar la nota'), 'error');
        }
    },
};
